import { useSyncExternalStore } from "react";
import { coverUrl, setOfflineUrls, streamUrl } from "@/lib/api";

const AUDIO_CACHE = "rh-audio-v1";
const COVER_CACHE = "rh-covers-v1";
const CAP_KEY = "rh.offlineCapMB";
export const DEFAULT_CAP_MB = 2048;
export const CAP_OPTIONS = [512, 1024, 2048, 5120, 10240];

export const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "caches" in window && "indexedDB" in window;
export const getCapMB = () => Number(localStorage.getItem(CAP_KEY)) || DEFAULT_CAP_MB;
export const setCapMB = (mb) => { localStorage.setItem(CAP_KEY, String(mb)); emit(); };

function openDb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open("resonance-offline", 1);
    r.onupgradeneeded = () => r.result.createObjectStore("pins", { keyPath: "key" });
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const t = db.transaction("pins", mode);
    const out = fn(t.objectStore("pins"));
    t.oncomplete = () => { db.close(); res(out?.result ?? out); };
    t.onerror = () => { db.close(); rej(t.error); };
  });
}
const allPins = () => tx("readonly", (s) => s.getAll());
const putPin = (p) => tx("readwrite", (s) => s.put(p));
const delPin = (key) => tx("readwrite", (s) => s.delete(key));

let state = { pins: [], jobs: {}, loaded: false };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const set = (patch) => {
  state = { ...state, ...patch };
  if (patch.pins) setOfflineUrls(Object.fromEntries(patch.pins.flatMap((p) => p.tracks.map((t) => [t.id, t.offline_url]))));
  emit();
};
export const useOffline = () => useSyncExternalStore((fn) => { listeners.add(fn); return () => listeners.delete(fn); }, () => state);
export const getState = () => state;

export async function loadPins() {
  if (!supported()) { set({ loaded: true }); return; }
  try { set({ pins: await allPins(), loaded: true }); } catch { set({ loaded: true }); }
}

export const pinKey = (type, id) => `${type}:${id}`;
export const findPin = (type, id) => state.pins.find((p) => p.key === pinKey(type, id));
export const usedBytes = () => state.pins.reduce((s, p) => s + (p.bytes || 0), 0);
export const fmtBytes = (b) => (b >= 1e9 ? `${parseFloat((b / 1e9).toFixed(2))} GB` : b >= 1e6 ? `${Math.round(b / 1e6)} MB` : `${Math.round(b / 1e3)} KB`);
export const fmtCap = (mb) => (mb >= 1024 ? `${parseFloat((mb / 1024).toFixed(1))} GB` : `${mb} MB`);

const setJob = (key, job) => set({ jobs: job ? { ...state.jobs, [key]: job } : Object.fromEntries(Object.entries(state.jobs).filter(([k]) => k !== key)) });

async function fetchInto(cache, url) {
  const r = await fetch(url, { credentials: "include", headers: { "X-RH-Pin": "1" } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const blob = await r.blob();
  await cache.put(url, new Response(blob, { headers: { "Content-Type": r.headers.get("Content-Type") || blob.type || "audio/mpeg", "Content-Length": String(blob.size) } }));
  return blob.size;
}

export async function pin({ type, id, name, subtitle, tracks, coverAlbumIds = [], userId, lib, auto = false }) {
  if (!supported()) throw new Error("Offline listening isn't supported in this browser");
  const key = pinKey(type, id);
  if (state.jobs[key]) return;
  const previous = state.pins.find((p) => p.key === key);
  const had = new Set((previous?.tracks || []).map((t) => t.id));
  const estimate = tracks.filter((t) => !had.has(t.id)).reduce((s, t) => s + (t.size || 0), 0);
  const cap = getCapMB() * 1024 * 1024;
  if (usedBytes() - (previous?.bytes || 0) + estimate > cap) {
    const err = new Error(`This needs about ${fmtBytes(estimate)} but only ${fmtBytes(Math.max(cap - usedBytes(), 0))} of your ${fmtBytes(cap)} offline limit is free. Raise the limit in Settings or unpin something first.`);
    err.code = "CAP";
    throw err;
  }
  setJob(key, { done: 0, total: tracks.length, bytes: 0 });
  const audio = await caches.open(AUDIO_CACHE);
  const covers = await caches.open(COVER_CACHE);
  let bytes = 0;
  const urls = {};
  try {
    for (const albumId of new Set([...coverAlbumIds, ...tracks.map((t) => t.album_id).filter(Boolean)])) {
      try { await fetchInto(covers, coverUrl(albumId)); } catch { /* cover is optional */ }
    }
    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i];
      const url = streamUrl(t.id);
      urls[t.id] = url;
      const existing = await audio.match(url, { ignoreVary: true });
      bytes += existing ? Number(existing.headers.get("Content-Length")) || t.size || 0 : await fetchInto(audio, url);
      setJob(key, { done: i + 1, total: tracks.length, bytes });
    }
    const record = { key, type, id, name, subtitle, tracks: tracks.map((t) => ({ ...t, offline_url: urls[t.id] })), coverAlbumIds, bytes, userId, lib, auto, pinned_at: previous?.pinned_at || new Date().toISOString(), synced_at: new Date().toISOString() };
    await putPin(record);
    const others = state.pins.filter((p) => p.key !== key);
    const keep = new Set([...others, record].flatMap((p) => p.tracks.map((t) => t.offline_url)));
    for (const t of previous?.tracks || []) if (!keep.has(t.offline_url)) await audio.delete(t.offline_url, { ignoreVary: true });
    set({ pins: [...others, record] });
  } finally {
    setJob(key, null);
  }
}

export async function unpin(type, id) {
  const key = pinKey(type, id);
  const rec = state.pins.find((p) => p.key === key);
  if (!rec) return;
  const others = state.pins.filter((p) => p.key !== key);
  const stillUsed = new Set(others.flatMap((p) => p.tracks.map((t) => t.offline_url)));
  const audio = await caches.open(AUDIO_CACHE);
  for (const t of rec.tracks) if (!stillUsed.has(t.offline_url)) await audio.delete(t.offline_url, { ignoreVary: true });
  await delPin(key);
  set({ pins: others });
}

export async function clearAll() {
  await caches.delete(AUDIO_CACHE);
  await caches.delete(COVER_CACHE);
  for (const p of state.pins) await delPin(p.key);
  set({ pins: [] });
}

export async function setPinAuto(type, id, auto) {
  const rec = state.pins.find((p) => p.key === pinKey(type, id));
  if (!rec || rec.auto === auto) return;
  const next = { ...rec, auto };
  await putPin(next);
  set({ pins: state.pins.map((p) => (p.key === next.key ? next : p)) });
}

export const isCached = async (trackId) => !!(await (await caches.open(AUDIO_CACHE)).match(streamUrl(trackId), { ignoreVary: true }));

export const PLAYER_KEY = "rh.player";
export const savePlayer = (snap) => { try { localStorage.setItem(PLAYER_KEY, JSON.stringify(snap)); } catch { /* quota */ } };
export const loadPlayer = () => { try { return JSON.parse(localStorage.getItem(PLAYER_KEY) || "null"); } catch { return null; } };
